import {
  ArrowLeftIcon,
  CircleNotchIcon,
  InfoIcon,
  LifebuoyIcon,
  PaperPlaneTiltIcon,
} from "@phosphor-icons/react";
import type { HelpRequestWithDetails } from "@reactive-resume/dto";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import { useToast } from "@/client/hooks/use-toast";
import { USER_SPACE_PATHS } from "@/client/pages/client/user-space/config/navigation";
import { helpApiError } from "@/client/services/help/api";
import { CATEGORY_META } from "@/client/services/help/display";
import { useCreateHelpRequest } from "@/client/services/help/queries";

const MESSAGE_MIN = 10;
const MESSAGE_MAX = 4000;

/**
 * Dialog de création d'une demande d'aide — 2 étapes, ZÉRO champ titre :
 *  1. choisir le thème (c'est le « titre » métier de la demande) ;
 *  2. décrire le problème — le sujet est généré automatiquement côté
 *     serveur (thème + première phrase) et le contexte technique (page,
 *     navigateur) est joint automatiquement pour accélérer le diagnostic.
 * Plusieurs demandes peuvent être ouvertes en parallèle (plafond serveur).
 */
export const NewHelpRequestDialog = ({
  open,
  onOpenChange,
  openRequest,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Demande en cours (OPEN/IN_PROGRESS) — information contextuelle, non bloquante. */
  openRequest?: HelpRequestWithDetails | null;
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createRequest = useCreateHelpRequest();

  const [step, setStep] = useState<1 | 2>(1);
  const [category, setCategory] = useState<string>("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<{ category?: string; message?: string }>({});

  const reset = () => {
    setStep(1);
    setCategory("");
    setMessage("");
    setErrors({});
  };

  const close = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const goToDescription = () => {
    if (!category) {
      setErrors({ category: "Choisissez un thème pour continuer" });
      return;
    }
    setErrors({});
    setStep(2);
  };

  const submit = () => {
    const nextErrors: typeof errors = {};
    if (!category) nextErrors.category = "Choisissez un thème";
    if (message.trim().length < MESSAGE_MIN)
      nextErrors.message = `Décrivez le problème (${MESSAGE_MIN} caractères minimum)`;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    createRequest.mutate(
      {
        category: category as never,
        message: message.trim(),
        context: {
          page: window.location.pathname,
          userAgent: navigator.userAgent,
        },
      },
      {
        onSuccess: (created) => {
          toast({
            title: "Demande envoyée",
            description: `Votre référence : ${created.code}. L'équipe vous répond par e-mail et ici même.`,
          });
          close(false);
          navigate(`${USER_SPACE_PATHS.help}/${created.id}`);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Envoi impossible",
            description: helpApiError(error, "Une erreur est survenue. Réessayez."),
          });
        },
      },
    );
  };

  const selectedMeta = category ? CATEGORY_META[category as keyof typeof CATEGORY_META] : null;

  /** Aperçu du sujet auto-généré (miroir indicatif de la règle serveur). */
  const autoSubject = selectedMeta
    ? `${selectedMeta.label}${
        message.trim() ? ` — ${(message.trim().split(/[.!?\n]/)[0] ?? "").trim().slice(0, 60)}` : ""
      }`
    : "";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] min-w-0 max-w-xl overflow-y-auto overflow-x-hidden sm:rounded-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
              <LifebuoyIcon aria-hidden size={22} weight="duotone" />
            </span>
            <div>
              <DialogTitle className="text-left text-lg font-bold tracking-tight text-slate-900">
                Nouvelle demande d'aide
              </DialogTitle>
              <DialogDescription className="text-left text-xs text-slate-500">
                {step === 1
                  ? "Choisissez un thème — l'équipe vous répond ici et par e-mail."
                  : "Décrivez votre problème — le sujet est généré automatiquement."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {openRequest && (
          <div className="flex items-start gap-2.5 rounded-xl bg-sky-50 p-3.5 text-sky-800 ring-1 ring-sky-200">
            <InfoIcon aria-hidden size={18} weight="duotone" className="mt-0.5 shrink-0" />
            <p className="text-[13px] leading-5">
              Une demande ({openRequest.code}) est déjà en cours.{" "}
              <button
                type="button"
                className="font-semibold underline underline-offset-2"
                onClick={() => {
                  close(false);
                  navigate(`${USER_SPACE_PATHS.help}/${openRequest.id}`);
                }}
              >
                Consultez-la
              </button>{" "}
              — vous pouvez aussi ouvrir une demande pour un sujet différent.
            </p>
          </div>
        )}

        {step === 1 ? (
          <div className="min-w-0 space-y-4">
            <div className="min-w-0 space-y-1.5">
              <p className="text-[13px] font-semibold text-slate-700">
                Quel est le sujet de votre demande ?
              </p>
              <p className="text-[11px] text-slate-400">
                Le thème sert de référence de suivi — aucun titre à rédiger.
              </p>

              <div className="grid min-w-0 grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                {Object.entries(CATEGORY_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  const selected = category === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setCategory(key);
                        setErrors({});
                      }}
                      className={cn(
                        "flex w-full min-w-0 items-start gap-2.5 rounded-xl border border-slate-200 p-2.5 text-left transition-all duration-150 hover:border-slate-300 hover:bg-slate-50",
                        selected && meta.selected,
                      )}
                    >
                      <span
                        className={cn(
                          "grid size-8 shrink-0 place-items-center rounded-lg",
                          meta.tile,
                        )}
                      >
                        <Icon aria-hidden size={16} weight="duotone" />
                      </span>
                      <span className="min-w-0">
                        <span className="block break-words text-[13px] font-semibold text-slate-800">
                          {meta.label}
                        </span>
                        <span className="mt-0.5 block break-words text-[11px] leading-4 text-slate-500">
                          {meta.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.category && (
                <p className="text-xs font-medium text-red-600">{errors.category}</p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => close(false)}>
                Annuler
              </Button>
              <Button type="button" onClick={goToDescription}>
                Continuer
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="min-w-0 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            {/* Thème sélectionné + aperçu du sujet auto */}
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-xl bg-indigo-50/60 p-3 ring-1 ring-indigo-100">
              <div className="flex min-w-0 items-center gap-2.5">
                {selectedMeta && (
                  <span
                    className={cn(
                      "grid size-9 shrink-0 place-items-center rounded-lg",
                      selectedMeta.tile,
                    )}
                  >
                    <selectedMeta.icon aria-hidden size={17} weight="duotone" />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800">{selectedMeta?.label}</p>
                  <p className="truncate text-[11px] text-slate-500" title={autoSubject}>
                    Sujet auto : {autoSubject}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex shrink-0 items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline"
              >
                <ArrowLeftIcon aria-hidden size={12} weight="bold" />
                Changer
              </button>
            </div>

            {/* Message */}
            <div className="space-y-1.5">
              <label htmlFor="help-message" className="text-[13px] font-semibold text-slate-700">
                Description du problème
              </label>
              <textarea
                id="help-message"
                value={message}
                maxLength={MESSAGE_MAX}
                onChange={(event) => setMessage(event.target.value)}
                rows={5}
                placeholder="Expliquez ce qui s'est passé : ce que vous faisiez, ce que vous attendiez, ce qui s'est affiché…"
                className={cn(
                  "shadow-xs w-full min-w-0 resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100",
                  errors.message && "border-red-300 focus:ring-red-100",
                )}
              />
              <div className="flex items-center justify-between">
                {errors.message ? (
                  <p className="text-xs font-medium text-red-600">{errors.message}</p>
                ) : (
                  <p className="text-[11px] text-slate-400">
                    Contexte technique (page, navigateur) joint automatiquement.
                  </p>
                )}
                <p className="text-[11px] tabular-nums text-slate-400">
                  {message.length}/{MESSAGE_MAX}
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button type="submit" disabled={createRequest.isPending}>
                {createRequest.isPending ? (
                  <CircleNotchIcon aria-hidden size={16} className="animate-spin" />
                ) : (
                  <PaperPlaneTiltIcon aria-hidden size={16} weight="fill" />
                )}
                Envoyer la demande
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
